import dayjs from 'dayjs';
import calendar from 'dayjs/plugin/calendar';
import isSameOrBefore from 'dayjs/plugin/isSameOrBefore';
import utc from 'dayjs/plugin/utc';

import { PATIENTS_AGE_LIMITS } from './constants';

export enum DateFormat {
  D = 'D', // 1
  ddd = 'ddd', // Sun
  dddd = 'dddd', // Sunday
  dddd_MMMM_D_h_mm_A = 'dddd, MMMM D · h:mm A', // Sunday, January 1 · 1:00 AM
  // dddd_MMMM_D_h_mma_z = 'dddd, MMMM D · h:mm a z', // Sunday, January 1 · 1:00am EST
  h_A = 'h A', // 1:00 am
  h_mm_a = 'h:mm a', // 1:00 am
  h_mma_z = 'h:mm a z', // 1:00 am EST
  // hh_mma = '(hh:mm a)', // (01:00 am)
  hh_mma_z = 'hh:mm a z', // 01:00 am EST
  MM_DD = 'MM/DD', // 01/01
  MM_DD_YYYY = 'MM/DD/YYYY', // 01/01/2018
  MMM = 'MMM', // Jan
  // MMMM_D_h_mma_z = 'MMMM D, h:mm a z', // January 1, 1:00 am EST
  // MMMM_YYYY = 'MMMM YYYY', // January 2018
  MMM_D = 'MMM D', // Jan 1
  MMM_D_h_mma = 'MMM D · h:mma', // Jan 1 · 1:00am
  MMM_D_h_mma_z = 'MMM D · h:mm a z',
  MMM_D_YYYY_h_mma_z = 'MMM D · YYYY [at] h:mm a', // Jan 01 · 2018 at 1:00 am
  MMM_DD = 'MMM DD', // Jan 01
  MMM_DD_YYYY = 'MMM DD, YYYY', // May 25, 2024
  MMM_YY = "MMM 'YY", // May '24
  MMMM_D = 'MMMM D', // January 1
  MMMM_DD_YYYY = 'MMMM DD, YYYY', // May 25, 2024
  /** Use this format for BE requests */
  YYYY_MM_DD = 'YYYY-MM-DD' // 2023-06-06
  // z = 'z' // EST
}

dayjs.extend(calendar);
dayjs.extend(isSameOrBefore);
dayjs.extend(utc);

/**
 * Formats a MarketONE date that is normalized to midnight UTC — `subscriptionEndDate` and the
 * `nextBillingAt` derived from it.
 *
 * These must be read in UTC. Plain `dayjs()` converts to the viewer's zone and renders the
 * previous day for anyone west of Greenwich — an Aug 12 billing date showing as "Aug 11", which is
 * the whole point of the copy read wrong.
 */
export const formatUtcShortDate = (date?: string): string | undefined =>
  date ? dayjs.utc(date).format(DateFormat.MMM_DD) : undefined;

/**
 * Formats a MarketONE event instant — `cancelledAt` and the `unlinkedAt` derived from it.
 *
 * Deliberately NOT the UTC reader above. These are real timestamps rather than normalized dates,
 * so a cancellation recorded at 02:30Z happened the previous evening for a US patient and must
 * render as the day they experienced, not the UTC day.
 */
export const formatLocalShortDate = (date?: string): string | undefined =>
  date ? dayjs(date).format(DateFormat.MMM_DD) : undefined;

export const validateBirth = (
  val: string,
  message: string,
  ageValidation = false,
  maxAge?: number
) => {
  const date = dayjs(val, DateFormat.MM_DD_YYYY);
  const maxAgeValue = maxAge || PATIENTS_AGE_LIMITS.MAX;
  if (!date.isValid()) return 'Please use the correct format: MM/DD/YYYY';

  const age = dayjs().diff(date, 'year');
  const isValidAge = ageValidation ? age >= PATIENTS_AGE_LIMITS.MIN && age <= maxAgeValue : true;

  return (isValidAge && date.format(DateFormat.MM_DD_YYYY) === val) || message;
};

export const isSupportedAge = (dob: string) => {
  const age = dayjs().diff(dayjs(dob).format(DateFormat.YYYY_MM_DD), 'year');
  if (Number.isNaN(age)) {
    throw new Error('invalid dob');
  }
  return age >= PATIENTS_AGE_LIMITS.MIN && age <= PATIENTS_AGE_LIMITS.MAX;
};

export const checkAgeSupportedOnlyByMedicare = (dob: string) => {
  const age = dayjs().diff(dayjs(dob).format(DateFormat.YYYY_MM_DD), 'year');
  if (Number.isNaN(age)) {
    throw new Error('invalid dob');
  }
  return age > PATIENTS_AGE_LIMITS.MAX && age < PATIENTS_AGE_LIMITS.MAX_FOR_MEDICARE;
};

export const parseTime = (time: string) => {
  return dayjs(time).calendar(null, {
    lastDay: '[yesterday •] h:mm A z',
    lastWeek: '[last] dddd [•] h:mm A z',
    nextDay: '[Tomorrow •] h:mm A z',
    nextWeek: 'dddd [•] h:mm A z',
    sameDay: '[Today •] h:mm A z',
    sameElse: `MM/DD/YYYY [•] h:mm A z`
  });
};

export const getDateValue = (date: string) => {
  if (date) {
    return `${
      dayjs(date.toString()).isToday()
        ? 'Today, '
        : dayjs(date.toString()).isTomorrow()
          ? 'Tomorrow, '
          : `${dayjs(date.toString()).format(DateFormat.dddd)}, `
    } ${dayjs(date.toString()).format(DateFormat.MMMM_D)}: `;
  }

  return '';
};

export const findDisabledDates = (
  datesWithSlots: string[],
  periodStart: string,
  periodEnd: string
) => {
  const disabledDates = [];
  const start = dayjs(periodStart);
  const end = dayjs(periodEnd);
  for (let m = start; m.isSameOrBefore(end); m = m.add(1, 'day')) {
    if (!datesWithSlots.includes(m.format(DateFormat.YYYY_MM_DD))) {
      disabledDates.push(m.format(DateFormat.YYYY_MM_DD));
    }
  }
  return disabledDates;
};
